import React, { useState } from 'react';
import axios from 'axios'
import { useEffect, useRef } from 'react';

// Configure axios to include CSRF token in all requests
const csrfToken = document.querySelector('[name="csrf-token"]')?.content;

const sparkServerUrl = window.location.hostname === 'localhost'
                    ? 'http://localhost:3001'
                    : window.location.origin;


if (csrfToken) {
  axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
}

// Make axios available globally for sessionManager to update CSRF token
window.axios = axios;

// Add axios response interceptor to handle 401 errors (session expired)
let sessionExpiredShown = false;

// Function to reset sessionExpiredShown flag (called when session is restored)
export function resetSessionExpiredFlag() {
  sessionExpiredShown = false;
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Session has expired - show modal only once
      if (!sessionExpiredShown) {
        sessionExpiredShown = true;

        // Notify sessionManager so visibility check can trigger restore callback
        import('./utils/sessionManager').then(({ default: sessionManager }) => {
          sessionManager.isSessionExpired = true;
        });

        // Import toast at top of file if not already imported
        import('react-toastify').then(({ toast }) => {
          toast.error('Your session has expired. Please log in again.', {
            autoClose: 5000,
            onClose: () => {
              window.location.href = '/users/sign_in';
            }
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

// Gentle theme styles for school_admin pages
// Uses CSS variables from ThemeManager but avoids aggressive !important overrides
// that break Semantic UI form elements, inputs, and modals
const schoolAdminThemeStyles = `
  /* ===== SCHOOL ADMIN GENTLE THEME ===== */

  /* Full page background - theme the html/body so no white bleeds through */
  html, body {
    background-color: var(--background) !important;
    color: var(--foreground) !important;
  }
  .ui.container {
    background-color: var(--background) !important;
  }

  /* Page background and text */
  #school-admin-root,
  #school-admin-root.ui.container {
    background-color: var(--background) !important;
    color: var(--foreground);
  }
  #school-admin-root > div {
    background-color: var(--background) !important;
  }

  /* Grid and rows */
  #school-admin-root .ui.grid {
    background-color: var(--background) !important;
  }
  #school-admin-root .ui.grid > .row {
    background-color: var(--background) !important;
    color: var(--foreground) !important;
  }

  /* Headings */
  #school-admin-root .ui.header,
  #school-admin-root h3.ui.header {
    color: var(--foreground) !important;
    border-bottom-color: var(--border) !important;
  }

  /* Links */
  #school-admin-root a {
    color: var(--primary);
  }
  #school-admin-root a:hover {
    color: var(--primary);
    opacity: 0.85;
  }

  /* Info message boxes */
  #school-admin-root .ui.info.message {
    background-color: var(--accent);
    border: 1px solid var(--border);
    color: var(--foreground);
    box-shadow: none;
  }
  #school-admin-root .ui.info.message .header {
    color: var(--primary) !important;
  }

  /* Negative message */
  #school-admin-root .ui.negative.message {
    background-color: var(--card);
    border-left: 4px solid var(--destructive);
  }

  /* Table styling */
  #school-admin-root .ui.table {
    background: var(--card);
    color: var(--card-foreground);
    border-color: var(--border);
  }
  #school-admin-root .ui.table thead th {
    background-color: var(--muted) !important;
    color: var(--foreground) !important;
    border-bottom-color: var(--border) !important;
  }
  #school-admin-root .ui.table tbody tr {
    background-color: var(--card);
  }
  #school-admin-root .ui.table tbody tr:hover {
    background-color: var(--accent) !important;
  }
  #school-admin-root .ui.table td {
    border-top-color: var(--border);
    color: var(--card-foreground);
  }

  /* Cards / elevated surfaces */
  #school-admin-root div[style*="boxShadow"],
  #school-admin-root div[style*="box-shadow"] {
    background-color: var(--card);
  }

  /* Primary buttons - use theme primary */
  #school-admin-root .ui.primary.button,
  #school-admin-root .ui.primary.buttons .button {
    background-color: var(--primary);
    color: var(--primary-foreground);
  }
  #school-admin-root .ui.primary.button:hover {
    background-color: var(--primary);
    opacity: 0.9;
  }

  /* Positive / green buttons */
  #school-admin-root .ui.positive.button,
  #school-admin-root .ui.green.button {
    background-color: var(--primary);
    color: var(--primary-foreground);
  }

  /* Basic buttons */
  #school-admin-root .ui.basic.button {
    color: var(--foreground) !important;
    border-color: var(--border) !important;
    background: var(--card) !important;
  }
  #school-admin-root .ui.basic.primary.button {
    color: var(--primary) !important;
    border-color: var(--primary) !important;
    background: transparent !important;
  }

  /* Secondary menu (tab navigation) */
  #school-admin-root .ui.secondary.menu .item {
    color: var(--muted-foreground);
  }
  #school-admin-root .ui.secondary.menu .active.item {
    color: var(--primary) !important;
    border-color: var(--primary) !important;
    background-color: var(--accent);
    font-weight: bold;
  }
  #school-admin-root .ui.secondary.menu .item:hover {
    color: var(--foreground);
    background-color: var(--muted);
  }

  /* Tab panes */
  #school-admin-root .ui.tab.segment,
  #school-admin-root .ui.attached.tabular.menu .item.active {
    background-color: var(--card);
    color: var(--card-foreground);
    border-color: var(--border);
  }
  #school-admin-root .ui.attached.tabular.menu .item {
    color: var(--muted-foreground);
  }
  #school-admin-root .ui.attached.tabular.menu .item.active {
    color: var(--foreground);
  }
  #school-admin-root .ui.bottom.attached.segment {
    background-color: var(--card);
    border-color: var(--border);
  }

  /* Modal - gentle themed */
  #school-admin-root ~ .ui.modal,
  .ui.modals #school-admin-root ~ .ui.modal,
  body > .ui.dimmer .ui.modal {
    background-color: var(--card);
    color: var(--card-foreground);
  }
  body > .ui.dimmer .ui.modal > .header {
    background-color: var(--card);
    color: var(--card-foreground);
    border-bottom-color: var(--border);
  }
  body > .ui.dimmer .ui.modal > .content {
    background-color: var(--card);
    color: var(--card-foreground);
  }
  body > .ui.dimmer .ui.modal > .actions {
    background-color: var(--muted);
    border-top-color: var(--border);
  }

  /* Breadcrumb */
  #school-admin-root .ui.breadcrumb a {
    color: var(--primary) !important;
  }
  #school-admin-root .ui.breadcrumb .active.section {
    color: var(--foreground);
  }
  #school-admin-root .ui.breadcrumb .divider {
    color: var(--muted-foreground);
  }

  /* Dropdown */
  #school-admin-root .ui.dropdown .menu {
    background-color: var(--card);
    border-color: var(--border);
  }
  #school-admin-root .ui.dropdown .menu > .item {
    color: var(--card-foreground) !important;
  }
  #school-admin-root .ui.dropdown .menu > .item:hover {
    background-color: var(--accent) !important;
    color: var(--accent-foreground) !important;
  }

  /* Loader */
  #school-admin-root .ui.dimmer {
    background-color: var(--background);
    opacity: 0.92;
  }
  #school-admin-root .ui.inverted.dimmer {
    background-color: var(--background);
  }

  /* Segment */
  #school-admin-root .ui.segment {
    background: var(--card);
    border-color: var(--border);
    color: var(--card-foreground);
  }

  /* Subtle text color overrides */
  #school-admin-root p {
    color: var(--foreground);
  }
`;

// Add custom styles for modal dimmer and theme variables
// NOTE: Color CSS variables are now managed by ThemeManager.js
// Only layout, font, and utility styles are defined here
const customStyles = `
  /* ===== THEME CSS VARIABLES ===== */
  /* Colors are dynamically applied by ThemeManager.js - see /themes/ThemeManager.js */
  /* Only fonts and non-color properties are defined here as fallbacks */
  :root {
    --font-sans: 'Libre Baskerville', serif;
    --font-serif: 'Lora', serif;
    --font-mono: 'IBM Plex Mono', monospace;
  }


  /* ===== MODAL DIMMER STYLES ===== */
  .ui.dimmer {
    background-color: oklch(0.2747 0.0139 57.6523 / 0.85) !important;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 30000 !important;
  }
  
  .ui.page.dimmer {
    background-color: oklch(0.2747 0.0139 57.6523 / 0.85) !important;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 30000 !important;
  }
  
  .ui.blurring.dimmer {
    background-color: oklch(0.2747 0.0139 57.6523 / 0.85) !important;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 30000 !important;
  }
  
  .ui.inverted.dimmer {
    padding: 4rem;
    background-color: var(--card) !important;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 30000 !important;
  }
  
  /* Add a subtle animation to the dimmer */
  .ui.dimmer.visible {
    animation: dimmerFadeIn 0.3s ease-out;
  }
  
  @keyframes dimmerFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* ===== LAB LAYOUT THEMED STYLES ===== */
  .lab-panel {
    background-color: var(--card);
    color: var(--card-foreground);
    border-color: var(--border);
    box-shadow: var(--shadow);
  }

  .lab-sidebar {
    background-color: var(--sidebar);
    color: var(--sidebar-foreground);
    border-color: var(--sidebar-border);
  }

  .lab-modal-header {
    background-color: var(--primary);
    color: var(--primary-foreground);
  }

  /* Fix for form labels */
  .ui.form .field > label {
    color: var(--card-foreground) !important;
  }

  /* Fix for input and placeholder backgrounds */
  .ui.input input, 
  .ui.form input:not([type=button]):not([type=submit]):not([type=reset]),
  .ui.form textarea {
    background-color: transparent !important;
    color: var(--card-foreground) !important;
    caret-color: var(--card-foreground) !important;
    border: none !important; /* Hide default inner border */
    box-shadow: none !important;
  }
  
  .lab-input input,
  .lab-input textarea,
  .ui.input.lab-input input {
    background-color: transparent !important;
    color: var(--accent-foreground) !important;
    caret-color: var(--accent-foreground) !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  .ui.input input::placeholder,
  .ui.form input::placeholder,
  .lab-input input::placeholder,
  .lab-input textarea::placeholder {
    background-color: transparent !important;
    color: var(--foreground) !important;
    opacity: 0.7; /* High contrast but still distinct from text */
  }

  .lab-input {
    background-color: var(--accent) !important;
    border: 1px solid var(--border) !important;
    color: var(--accent-foreground) !important;
    border-radius: var(--radius) !important;
    transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
    overflow: hidden !important;
  }

  .lab-input:focus-within {
    border-color: var(--primary) !important;
    box-shadow: 0 0 0 1px var(--primary) !important;
  }

  .lab-button-primary {
    background-color: var(--primary);
    color: var(--primary-foreground);
  }

  .lab-button-secondary {
    background-color: var(--secondary);
    color: var(--secondary-foreground);
  }

  .lab-muted-text {
    color: var(--muted-foreground);
  }

  .lab-destructive {
    background-color: var(--destructive);
    color: var(--destructive-foreground);
  }

  /* ===== TOAST NOTIFICATION THEME ===== */
  .Toastify__toast {
    background-color: var(--card) !important;
    color: var(--card-foreground) !important;
    border: 1px solid var(--border) !important;
    border-radius: 8px !important;
    box-shadow: var(--shadow-lg) !important;
    padding: 12px !important;
  }
  .Toastify__toast--default, .Toastify__toast--info, .Toastify__toast--warning {
    background-color: var(--card) !important;
    color: var(--card-foreground) !important;
  }
  .Toastify__toast-body {
    font-family: inherit !important;
    font-size: 0.95em !important;
    color: var(--card-foreground) !important;
  }
  .Toastify__close-button {
    color: var(--card-foreground) !important;
    opacity: 0.6;
  }
  .Toastify__progress-bar {
    background: var(--primary) !important;
  }
  .Toastify__toast--success {
    background-color: #07bc0c !important;
    color: #fff !important;
    border-left: 5px solid #07bc0c !important;
  }
  .Toastify__toast--success .Toastify__toast-body {
    color: #fff !important;
  }
  .Toastify__toast--success .Toastify__close-button {
    color: #fff !important;
  }
  .Toastify__toast--error {
    background-color: #e74c3c !important;
    color: #fff !important;
    border-left: 5px solid var(--destructive) !important;
  }
  .Toastify__toast--error .Toastify__toast-body {
    color: #fff !important;
  }
  .Toastify__toast--error .Toastify__close-button {
    color: #fff !important;
  }
  .Toastify__toast--warning {
    border-left: 5px solid var(--accent) !important;
  }
  .Toastify__toast--info, .Toastify__toast--default {
    border-left: 5px solid var(--primary) !important;
  }
  .Toastify__toast-icon svg {
    fill: var(--primary) !important;
  }
  .Toastify__toast--success .Toastify__toast-icon svg {
    fill: #22c55e !important;
  }
  .Toastify__toast--error .Toastify__toast-icon svg {
    fill: var(--destructive) !important;
  }

  /* ===== CHECKBOX VISIBILITY FIX ===== */
  #top-bar .ui.checkbox label,
  #top-bar .ui.checkbox label p {
    color: var(--primary) !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
  
  #top-bar .ui.checkbox label:before {
    border: 1px solid var(--primary) !important;
    background: var(--card) !important;
  }

  /* ===== QUILL EDITOR THEME STYLES (Notes Panel) ===== */
  .ql-toolbar.ql-snow {
    background-color: var(--muted) !important;
    border-color: var(--border) !important;
    border-radius: var(--radius) var(--radius) 0 0 !important;
  }
  
  .ql-toolbar.ql-snow .ql-stroke {
    stroke: var(--foreground) !important;
  }
  
  .ql-toolbar.ql-snow .ql-fill {
    fill: var(--foreground) !important;
  }
  
  .ql-toolbar.ql-snow .ql-picker {
    color: var(--foreground) !important;
  }
  
  .ql-toolbar.ql-snow .ql-picker-options {
    background-color: var(--card) !important;
    border-color: var(--border) !important;
  }
  
  .ql-toolbar.ql-snow button:hover,
  .ql-toolbar.ql-snow button:focus,
  .ql-toolbar.ql-snow button.ql-active,
  .ql-toolbar.ql-snow .ql-picker-label:hover,
  .ql-toolbar.ql-snow .ql-picker-label.ql-active,
  .ql-toolbar.ql-snow .ql-picker-item:hover,
  .ql-toolbar.ql-snow .ql-picker-item.ql-selected {
    color: var(--primary) !important;
  }
  
  .ql-toolbar.ql-snow button:hover .ql-stroke,
  .ql-toolbar.ql-snow button:focus .ql-stroke,
  .ql-toolbar.ql-snow button.ql-active .ql-stroke {
    stroke: var(--primary) !important;
  }
  
  .ql-toolbar.ql-snow button:hover .ql-fill,
  .ql-toolbar.ql-snow button:focus .ql-fill,
  .ql-toolbar.ql-snow button.ql-active .ql-fill {
    fill: var(--primary) !important;
  }
  
  .ql-container.ql-snow {
    background-color: var(--card) !important;
    border-color: var(--border) !important;
    border-radius: 0 0 var(--radius) var(--radius) !important;
    color: var(--card-foreground) !important;
  }
  
  .ql-editor {
    color: var(--card-foreground) !important;
  }
  
  .ql-editor.ql-blank::before {
    color: var(--muted-foreground) !important;
    opacity: 0.7;
  }
  
  .ql-bubble .ql-editor {
    color: var(--card-foreground) !important;
  }
  
  /* ===== SEMANTIC UI TAB THEME STYLES ===== */
  .ui.secondary.pointing.menu {
    border-bottom-color: var(--border) !important;
  }
  
  .ui.secondary.pointing.menu .item {
    color: var(--muted-foreground) !important;
    border-color: transparent !important;
  }
  
  .ui.secondary.pointing.menu .active.item {
    color: var(--foreground) !important;
    border-color: var(--primary) !important;
  }
  
  .ui.secondary.pointing.menu .item:hover {
    color: var(--foreground) !important;
  }
  
  .ui.tab.segment,
  .ui.attached.segment {
    background-color: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  /* ===== LOWLIGHT/HLJS SYNTAX HIGHLIGHTER THEME STYLES (NGSpice Panel) ===== */
  .hljs {
    background: transparent !important;
    color: var(--foreground) !important;
    padding: 1em !important;
    border-radius: var(--radius) !important;
  }
  
  .hljs-keyword,
  .hljs-selector-tag,
  .hljs-built_in,
  .hljs-name,
  .hljs-tag {
    color: var(--primary) !important;
  }
  
  .hljs-string,
  .hljs-title,
  .hljs-section,
  .hljs-attribute,
  .hljs-literal,
  .hljs-template-tag,
  .hljs-template-variable,
  .hljs-type {
    color: var(--chart-1) !important;
  }
  
  .hljs-number,
  .hljs-class .hljs-title,
  .hljs-function .hljs-title {
    color: var(--chart-2) !important;
  }
  
  .hljs-comment,
  .hljs-quote,
  .hljs-deletion,
  .hljs-meta {
    color: var(--muted-foreground) !important;
  }
  
  .hljs-variable,
  .hljs-template-variable,
  .hljs-link {
    color: var(--chart-3) !important;
  }
  
  .hljs-addition {
    color: var(--chart-4) !important;
  }
  
  .hljs-symbol {
    color: var(--chart-5) !important;
  }

  /* ===== MODAL THEME STYLES ===== */
  .ui.modal {
    background-color: var(--card) !important;
    color: var(--card-foreground) !important;
    border-radius: var(--radius) !important;
    overflow: hidden !important;
    z-index: 30001 !important;
  }
  .ui.modal > .header {
    background-color: var(--card) !important;
    color: var(--card-foreground) !important;
    border-bottom: 1px solid var(--border) !important;
    font-family: var(--font-sans) !important;
  }
  .ui.modal > .content {
    background-color: var(--card) !important;
    color: var(--card-foreground) !important;
  }
  .ui.modal > .actions {
    background-color: var(--card) !important;
    border-top: 1px solid var(--border) !important;
  }
  .ui.modal > .close.icon {
    color: var(--muted-foreground) !important;
    top: 1rem !important;
    right: 1rem !important;
  }
  .ui.modal > .close.icon:hover {
    color: var(--foreground) !important;
  }
  .ui.modal .ui.header {
    color: var(--card-foreground) !important;
  }
  
  /* Plain Secondary Menu (used in Share modal) */
  .ui.secondary.menu .item {
    color: var(--muted-foreground) !important;
  }
  .ui.secondary.menu .active.item {
    color: var(--foreground) !important;
    background: var(--accent) !important;
    color: var(--accent-foreground) !important;
    border-radius: var(--radius) !important;
  }
  .ui.secondary.menu .item:hover {
    color: var(--foreground) !important;
    background: var(--primary) !important; /* Slight difference for hover */
    color: var(--primary-foreground) !important;
  }
`;
// Remove LanguageSelector import - already removed
// import LanguageSelector from './LanguageSelector';
import useTimeTracker from './hooks/useTimeTracker';

import {
  Dropdown,
  Form,
  Button,
  Modal,
  Transition,
  Container,
  Label,
  GridRow,
  Icon
} from 'semantic-ui-react'
import { Fragment } from 'react';
import { Message, Confirm } from 'semantic-ui-react';

// import './LabLayout.css'; CAUTION! see notes below
// importing css causes issues when building the project at aws code build
// see app/assets/stylesheets/lab.css
// used at app/views/layouts/lab_layout.html.haml


import SideTabs from './SideTabs'

import { ToastContainer, toast } from 'react-toastify';
import PageVisibility from 'react-page-visibility';
import ActionMessage from './ActionMessage'
// Remove Zoom import since it's now handled in RightSection
// import Zoom from './Zoom'
import ShowOff from './ShowOff'
import SignedInAd from './SignedInAd'
import SponsorBanner from './SponsorBanner'

import IcCreatorWrapper from './ic-creator/src/IcCreatorWrapper';
import displayConsoleArt from './utils/consoleArt';

import {
  Grid,
  Advertisement,
  Header,
  Menu,
  Segment,
  Sidebar
} from 'semantic-ui-react'
import Share from "./Share";
import Chart from "./Chart";

import { handleOpenIcCreator } from "./utils/icCreator";
import { BasicAd } from "./BasicAd";
import LeftPanel from './LeftPanel/LeftPanel';
import AISideTabs from './Header/components/RightSection/AISideTabs';
import SubscriptionModal from './components/SubscriptionModal';
import SessionWarningModal from './components/SessionWarningModal';
import InviteTeacherModal from './components/InviteTeacherModal';
import useFirstSession from './activation/useFirstSession';
import StarterPanel from './activation/StarterPanel';
import FirstSessionChecklist from './activation/FirstSessionChecklist';
import { track as trackActivation } from './activation/track';
import { isPanelDismissed, setPanelDismissed } from './activation/storage';
import { isRTLLocale } from './activation/translations';
import useLayerPlacement from './activation/useLayerPlacement';
import { labTimeThresholds, guestHardLimitReleased } from './guestLimit';
import JsonFormsEditor from './components/JsonFormsEditor';
import './components/JsonFormsEditor.css';
import './components/JsonEditorHelper';
import sessionManager from './utils/sessionManager';
import TopBar from './TopBar';
import { initializeTheme } from './themes';
import ErrorBoundary from './components/ErrorBoundary';
import LabErrorFallback from './components/LabErrorFallback';
import SectionErrorFallback from './components/SectionErrorFallback';
import useLabCanvasGuard from './hooks/useLabCanvasGuard';
import { reportError } from './utils/sentry';
import MobileSlidesOverlay from './MobileSlidesOverlay';

const STARTER_KEYS = ['led', 'diode', 'ohm'];

// ?starter=<key> marks a visit that came from the first-session starter panel
const readStarterParam = () => {
  try {
    const key = new URLSearchParams(window.location.search).get('starter');
    return STARTER_KEYS.includes(key) ? key : null;
  } catch (e) {
    return null;
  }
};

function LabLayout(props) {
  // Client-side mobile detection - combines with server-side is_mobile prop
  const [windowIsMobile, setWindowIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    // Initial check
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Combine server-side and client-side mobile detection. Layout decisions
  // (top bar, slides column, ad/sponsor rows) use this, so a desktop window at
  // <=768px matches lab_layout.css's mobile rules (which hide the slides
  // column and the thumbnails row) instead of reserving blank space for them.
  // Device behaviour passed to the canvas (Thumbs) still uses props.is_mobile.
  const isMobile = props.is_mobile || windowIsMobile;

  // Add custom styles to the document head and initialize theme
  // On school_admin pages, initialize theme variables (for gentle color integration)
  // but skip injecting lab-specific Semantic UI overrides that break school_admin components
  useEffect(() => {
    // Always initialize theme so CSS variables are available on :root
    initializeTheme();

    // On school_admin pages, inject gentle theme styles instead of aggressive lab overrides
    if (document.getElementById('school-admin-root')) {
      const schoolStyleElement = document.createElement('style');
      schoolStyleElement.type = 'text/css';
      schoolStyleElement.appendChild(document.createTextNode(schoolAdminThemeStyles));
      document.head.appendChild(schoolStyleElement);
      return () => {
        document.head.removeChild(schoolStyleElement);
      };
    }

    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.appendChild(document.createTextNode(customStyles));
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const { current_locale } = props;
  const { allow_chat } = props;
  const { available_chats_count } = props;
  const { user_ics } = props;
  const { official_ics } = props;
  const { experiment_owner_ics } = props;
  const { looking_at_others_circuit } = props;
  let last_visiblity_change = null;
  // Use CSS custom properties for theming - these will be applied via inline styles
  let background = 'var(--sidebar)';
  let forecolor = 'var(--sidebar-foreground)';

  // Time thresholds from backend (with fallback defaults)
  // A server 0 is honoured: guests get tertiary 0, the hard limit from the start
  const timeThresholds = labTimeThresholds(props.time_thresholds);
  const PRIMARY_THRESHOLD = timeThresholds.primary;      // 1 minute
  const SECONDARY_THRESHOLD = timeThresholds.secondary;  // 2 minutes (soft limit)
  const TERTIARY_THRESHOLD = timeThresholds.tertiary;    // 10 minutes signed in, 0 for guests (hard limit)
  const [time_at_vsible, setTime_at_vsible] = useState(0);
  const [zoomValue, setZoomValue] = useState(1);

  const [is_labLoaded, setLabLoaded] = useState(false);

  // First-session activation: starter panel on the empty lab + checklist (server decides who)
  const firstSessionEnabled = props.first_session_eligible === true && !props.embed_mode;
  const starterKeyRef = useRef(readStarterParam());
  const starterLoadedRef = useRef(false);
  const [starterPanelDismissed, setStarterPanelDismissed] = useState(() => isPanelDismissed());
  const starterCircuits = Array.isArray(props.starter_circuits) ? props.starter_circuits : [];
  const starterPanelWanted = firstSessionEnabled && props.action_name === 'lab' && !props.experiment_id &&
    !props.is_breadboard_lab && starterCircuits.length > 0 && !starterPanelDismissed;
  const firstSessionSource = starterKeyRef.current ? 'starter'
    : (props.action_name === 'lab' && !props.experiment_id ? 'blank' : 'circuit');
  const firstSession = useFirstSession({
    enabled: firstSessionEnabled,
    isLabLoaded: is_labLoaded,
    includeSave: props.session?.is_subscription_active === true,
    source: firstSessionSource,
    isGuest: !props.session?.is_user_signed_in,
    watchBoardEmpty: starterPanelWanted,
    expectsCircuit: !!props.experiment_id
  });
  const showStarterPanel = starterPanelWanted && !firstSession.boardFilled;
  const firstSessionRTL = isRTLLocale(current_locale);
  const firstSessionLayerShown = is_labLoaded && firstSession.available && (firstSession.showChecklist || showStarterPanel);
  const firstSessionPlacement = useLayerPlacement({ enabled: firstSessionLayerShown, rtl: firstSessionRTL, isMobile });

  const handleDismissStarterPanel = () => {
    setPanelDismissed();
    setStarterPanelDismissed(true);
  };

  // starter_loaded fires on the starter's page once the lab is up; the param is then dropped so a
  // reload does not count it twice
  useEffect(() => {
    const which = starterKeyRef.current;
    if (!firstSessionEnabled || !is_labLoaded || !which || starterLoadedRef.current || props.embed_mode) return;
    starterLoadedRef.current = true;
    trackActivation('starter_loaded', { which });
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('starter');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (e) {
      // URL or history unavailable: keep the param
    }
  }, [is_labLoaded]);

  const [is_public, setPublic] = useState(false);
  const [isSaveOpened, setSaveOpened] = useState(false);
  const [isSaveSubmited, setSaveSubmited] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [isIcCreatorOpen, setIcCreatorOpen] = useState(false);

  const [isRetryOpened, setRetryOpened] = useState(false);
  const [customErrorOpened, setCustomError] = useState(false);

  // Session timeout warning states
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
  // Opened by the student CTA in ActionMessage
  const [inviteTeacherModalOpen, setInviteTeacherModalOpen] = useState(false);
  const [sessionMinutesLeft, setSessionMinutesLeft] = useState(5);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [share_opened, setShareOpen] = useState(false);

  const [mark, setMark] = useState(0);
  const [markCaption, setMarkCaption] = useState("");

  const [title, setTitle] = useState("");
  const [titleBefore, setTitleBefore] = useState("");
  const [experiment_id, setExperiment_id] = useState("");

  const [description, setDescription] = useState("");
  const [question, setQuestion] = useState("");
  const [analysisMode, setAnalysisMode] = useState('time_domain');
  const [DCSweepSources, setDCSweepSources] = useState([]);
  const [metricNodes, setMetricNodes] = useState([]);
  const [slides_collapsed, setSlidesCollapsed] = useState(false);
  // Embed mode keeps the slides panel (and the shortcuts panel under it) mounted but hidden: it drives lab loading.
  const slidesHidden = slides_collapsed || props.embed_mode === true;

  const [pickedDCSweepSource, setPickedDCSweepSource] = useState([]);

  // Tracks selections for each analysis mode to avoid losing them when switching back and forth
  const memoizedSelections = useRef({
    time_domain: { volt: [], component: [], metric: [] },
    ac_sweep: { volt: [], component: [], metric: [] },
    dc_sweep: { volt: [], component: [], metric: [] }
  });
  const prevModeRef = useRef(analysisMode);

  const [submitAnswerDialogShown, showSubmitedAnswerDialog] = useState(false);

  const [aiVisible, setAiVisible] = useState(() => {
    return (props.show_ai_chat && !props.experiment_id) || props.ai_chat_visible === true;
  });

  const [visible, setVisible] = React.useState(false)
  // Embed mode never opens the notes/NGSpice side panel next to the board.
  const notesOpen = visible && !props.embed_mode;
  const [nodes, setNodes] = React.useState([])
  const [selectedNodes, setSelectedNodes] = React.useState({ labels: [], datasets: [] })
  const [initialChecksSelected, setInitialChecksSelected] = React.useState([])
  const [isChartLoading, setChartLoading] = React.useState(false)
  const [show_ads, setShowAds] = React.useState(true)
  // Sponsor leaderboard props from the server (SponsorAds.show?); null = no banner.
  const [sponsorAd, setSponsorAd] = React.useState(props.sponsor_ad || null)
  const [chart_volts_data, setSelectedVoltsValues] = React.useState({ labels: [], datasets: [] })
  const [chart_VSweep_values, setVSweepValues] = React.useState({ labels: [], datasets: [] })
  const [chart_amperes_data, setSelectedAmperesValues] = React.useState({ labels: [], datasets: [] })
  const [labLoadingTimerHandler, setLabLoadingTimerHandler] = React.useState(null);

  const [currentable_names, set_currentable_names] = React.useState([])
  const [userProjects, setuserProjects] = React.useState([])
  const [show_ic_creator, setShowIcCreator] = React.useState(false)

  const [initialLocation, setInitialLocation] = React.useState({ x: 500, y: window.innerHeight - 250 })

  const [currentMouseEvent, setCurrentMouseEvent] = React.useState('up')


  const [user, setUser] = React.useState(props.user)
  const [submittedAt, setSubmittedAt] = React.useState(null)
  const [submission, setSubmision] = React.useState(null)
  const [labHeight, setLabHeight] = React.useState(500)
  const [isTeacher, setIsTeacher] = React.useState(null)
  const [circuit, setCircuit] = React.useState({})

  // State for subscription modal
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // State for JSON Forms editor modal - used by parent components to trigger schema-based editing
  const [editJson, setEditJson] = useState(null);  // Expects: { schema, uiSchema, data, onSave, title }

  // Track if we should show the modal based on thresholds
  const [shouldShowModalForPrimary, setShouldShowModalForPrimary] = useState(true);
  const [shouldShowModalForSecondary, setShouldShowModalForSecondary] = useState(true);
  const [shouldShowModalForTertiary, setShouldShowModalForTertiary] = useState(true);

  // Track which threshold triggered the modal
  const [currentModalThreshold, setCurrentModalThreshold] = useState(null);

  // Subscription and user state
  const [is_subscription_active, setis_subscription_active] = React.useState(props.is_subscription_active || false)
  const [privateDetected, setPrivateDetected] = React.useState(false)
  const [restrict_save, setRestrict_save] = React.useState(false)
  const [within_trial_period, setwithin_trial_period] = React.useState(false)
  const [is_user_signed_in, setis_user_signed_in] = React.useState(false)
  const [subscription_name, setSubscription_name] = React.useState("")
  const [userMenu, setUserMenu] = React.useState(() => {
    return Array.isArray(props.userMenu) ? props.userMenu : [];
  });

  // Track time spent on the lab page
  const {
    elapsedTime,
    formattedTime,
    thresholdReached,
    secondaryThresholdReached,
    tertiaryThresholdReached,
    resetPrimaryThreshold,
    resetSecondaryThreshold,
    resetTertiaryThreshold
  } = useTimeTracker({
    // Guests on the 0 s rule get their own key, so its reached hard limit (saved ~1 s after load) never mixes with the
    // older guest key's 10-minute record
    key: user && user.id ? `lab___timez___m200t___${user.id}`
      : (TERTIARY_THRESHOLD === 0 ? `lab___timez___m200t___guest0` : `lab___timez___m200t___`),
    threshold: PRIMARY_THRESHOLD,
    secondaryThreshold: SECONDARY_THRESHOLD,
    tertiaryThreshold: TERTIARY_THRESHOLD,
    trackWhenHidden: false,
    isLabLoaded: is_labLoaded, // Pass the state here
    onThresholdReached: (time) => {
      // Just log, we'll handle showing the modal in useEffect
      //console.log(`Primary time threshold reached: ${time} seconds spent on lab`);

      // For subscribed users, just show a notification
      if (is_subscription_active) {
        // instance("show_lab_alert", {
        //     text: `You've been working on this lab for ${PRIMARY_THRESHOLD} seconds!`,
        //     seconds: 5
        // });
      }
    },
    onSecondaryThresholdReached: (time) => {
      // Just log, we'll handle showing the modal in useEffect
      //console.log(`Secondary time threshold reached: ${time} seconds spent on lab`);
    },
    onTertiaryThresholdReached: (time) => {
      // Just log, we'll handle showing the modal in useEffect
      //console.log(`Tertiary time threshold reached: ${time} seconds spent on lab`);
    }
  });

  // Monitor Haxe/OpenFL canvas for crashes that React error boundaries cannot catch
  useLabCanvasGuard(is_labLoaded);

  // Embed mode: tell the circuit page that the board is drawn, so it can drop its poster image.
  useEffect(() => {
    if (!props.embed_mode || !is_labLoaded || window.parent === window) return undefined;
    const timer = setTimeout(() => window.parent.postMessage({ type: 'dcaclab:embed-ready' }, window.location.origin), 600);
    return () => clearTimeout(timer);
  }, [is_labLoaded]);

  // Expose JSON Forms Editor API for Haxe/JavaScript parent components
  // This provides a non-invasive way for any parent code to trigger schema-based editing
  useEffect(() => {
    // Store reference to setEditJson on window object for external access
    window.openJsonEditor = (config) => {
      // config should contain: { schema, uiSchema, data, onSave, title }
      if (config && config.schema) {
        setEditJson(config);
      } else {
        console.error('[LabLayout] openJsonEditor requires a schema object');
      }
    };

    // Cleanup on unmount
    return () => {
      delete window.openJsonEditor;
    };
  }, []); // Empty dependency array - setEditJson is stable

  // Reset and restore chart-related states when analysis mode changes
  useEffect(() => {
    // Skip on initial mount - let normal initialization happen
    // prevModeRef is initialized to analysisMode, so they'll be equal on first mount
    if (prevModeRef.current === analysisMode) {
      return;
    }

    // 1. Save current selections for the PREVIOUS mode
    if (prevModeRef.current && memoizedSelections.current[prevModeRef.current]) {
      memoizedSelections.current[prevModeRef.current] = {
        volt: [...(window.volt || [])],
        component: [...(window.component || [])],
        metric: [...(window.metric || [])]
      };
    }

    // 2. Clear current React states for a fresh start
    // NOTE: We do NOT clear setNodes([]) here to prevent the chart
    // from unmounting/disappearing during the transition.
    setSelectedNodes({ labels: [], datasets: [] });
    setInitialChecksSelected([]);
    setSelectedVoltsValues({ labels: [], datasets: [] });
    setSelectedAmperesValues({ labels: [], datasets: [] });
    setVSweepValues({ labels: [], datasets: [] });
    setChartLoading(true);

    // 3. Restore selections for the NEW mode
    const newSelections = memoizedSelections.current[analysisMode] || { volt: [], component: [], metric: [] };
    window.volt = [...newSelections.volt];
    window.component = [...newSelections.component];
    window.metric = [...newSelections.metric];

    // 4. Notify engine to clear/update selections
    if (typeof com !== 'undefined') {
      // A. Clear probes FIRST while still in the old mode to prevent incompatible simulation
      com.clientside.main.Html5Bootstrap.jsCall("setSelectedNodesNums", [], "volt");
      com.clientside.main.Html5Bootstrap.jsCall("setSelectedNodesNums", [], "component");
      com.clientside.main.Html5Bootstrap.jsCall("setSelectedNodesNums", [], "metric");

      // B. Change analysis mode in Haxe
      com.clientside.main.Html5Bootstrap.jsCall("setAnalysisMode", analysisMode);

      // B.1 Sync DC Sweep source if applicable
      if (analysisMode === 'dc_sweep' && pickedDCSweepSource) {
        com.clientside.main.Html5Bootstrap.jsCall("setDcSweepSource", pickedDCSweepSource);
      }

      // C. Restore probes AFTER the mode change is successful
      com.clientside.main.Html5Bootstrap.jsCall("setSelectedNodesNums", window.volt, "volt");
      com.clientside.main.Html5Bootstrap.jsCall("setSelectedNodesNums", window.component, "component");
      com.clientside.main.Html5Bootstrap.jsCall("setSelectedNodesNums", window.metric, "metric");

      // D. Explicitly trigger simulation once everything is in sync
      // A short timeout ensures the engine has processed state changes
      setTimeout(() => {
        if (typeof com !== 'undefined') {
          // Refresh ensures the netlist generator is in a clean state
          com.clientside.main.Html5Bootstrap.jsCall("refresh");
          com.clientside.main.Html5Bootstrap.jsCall("simulate");
        }
      }, 150);
    }

    // 5. Update prevModeRef for next time
    prevModeRef.current = analysisMode;
  }, [analysisMode]);

  async function checkReferrerViaBackend(url) {
    if (!url) return { status: 'no-referrer', accessible: false };

    try {
      const response = await fetch('/api/check_referrer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ url: url })
      });

      return await response.json();
    } catch (error) {
      return {
        status: 'error',
        accessible: false,
        error: error.message
      };
    }
  }
  // Handle showing the modal based on thresholds
  useEffect(() => {
    // Only for non-subscribers
    if (is_subscription_active) {
      com.clientside.main.Html5Bootstrap.jsCall("no-hard-limit-reached");
      return;
    }

    // Show modal for tertiary threshold if we should (highest priority)


    const originalReferrer = document.querySelector('meta[name="original-referrer"]')?.content;
    const lastReferrer = document.querySelector('meta[name="last-referrer"]')?.content;

    if (false && originalReferrer && !privateDetected) {
      setPrivateDetected(true);
      // Check in background (non-blocking)
      checkReferrerViaBackend(originalReferrer).then(result => {
        if (result.is_private) {
          com.clientside.main.Html5Bootstrap.jsCall("hard-limit-reached");
          setShowSubscriptionModal(true);
          if (typeof gtag === "function") {
            gtag("event", "private-referrer-block", { "event_category": "Lab", "event_label": "In Lab" });
          }
        }

      });
    }

    // Someone else's circuit only skips the on-load prompt (ActionMessage initialGuestShow); limits apply as on the lab.
    // Embeds (/iframe, bare or ?embed=1) stay unlimited, as before: ?embed=1 never renders the prompt and the bare
    // iframe is the public embed code, usually cross-site, where signing up in place does not work.
    if (props.action_name === 'iframe') return;

    // The engine must exist before it is told about limits; a guest's 0 s limit is reached on the first tick
    if (!is_labLoaded) return;

    // Signing in from another tab updates this tab in place (no reload): drop the guest-only limit here; the user's own
    // 10 minutes start on the next load. (Signing out in place keeps the page's thresholds until reload, as before.)
    if (guestHardLimitReleased({ tertiaryThreshold: TERTIARY_THRESHOLD, user })) {
      com.clientside.main.Html5Bootstrap.jsCall("no-hard-limit-reached");
      return;
    }

    if (tertiaryThresholdReached && shouldShowModalForTertiary) {
      com.clientside.main.Html5Bootstrap.jsCall("hard-limit-reached");

      if (typeof gtag === "function") {
        gtag("event", "hard-limit-reached", { "event_category": "Lab", "event_label": "In Lab" });
      }


      //setShowSubscriptionModal(true);
      setCurrentModalThreshold('tertiary');
      return;
    }

    // Show modal for secondary threshold if we should
    if (secondaryThresholdReached && shouldShowModalForSecondary) {
      com.clientside.main.Html5Bootstrap.jsCall("soft-limit-reached");


      if (typeof gtag === "function") {
        gtag("event", "soft-limit-reached", { "event_category": "Lab", "event_label": "In Lab" });
      }


      //setShowSubscriptionModal(true);
      setCurrentModalThreshold('secondary');
      return;
    }

    // Show modal for primary threshold if we should
    if (thresholdReached && shouldShowModalForPrimary) {
      //console.log("thresholdReached");
      //setShowSubscriptionModal(true);
      setCurrentModalThreshold('primary');
    }
  }, [thresholdReached, secondaryThresholdReached, tertiaryThresholdReached,
    shouldShowModalForPrimary, shouldShowModalForSecondary, shouldShowModalForTertiary,
    is_subscription_active, is_labLoaded, user]);

  useEffect(() => {
    if (showSubscriptionModal) {

    }
  }, [showSubscriptionModal]);

  const [triedToSave, setTriedToSave] = React.useState(0)

  const [currentSlide, setCurrentSlide] = React.useState(0)

  const [useNGSpice, setUseNGSpice] = useState(false)
  const [NGSpiceNetList, setNGSpiceNetList] = useState("")
  const [ngSpiceModelJsons, setNgSpiceModelJsons] = useState({})
  const [selectedComponentId, setSelectedComponentId] = useState(-1)
  const [limited, setLimited] = useState(false);
  const [cta, setCTA] = useState(null);
  const [limitation, setLimitation] = useState(false);

  const [descriptionShown, setDescriptionShown] = useState(false)

  const [mobileSlidesOpen, setMobileSlidesOpen] = useState(false)

  const force_show_thumbs = true;
  const action_name = props.action_name;
  // Pages that carry the sponsor leaderboard, decided by the server for this page
  // (ExperimentsHelper#sponsor_ad_page?: the lab, or a circuit's page that is not part of
  // an assignment). /api/current-user refreshes sponsorAd as for the lab, so the page's
  // own answer is kept here.
  const sponsorSlot = props.sponsor_ad_page === true;
  const show_save_buttons = ((action_name == 'lab' || (user && props.creator && user.id == props.creator.id))) && is_labLoaded && action_name !== 'assignment';
  const isResubmit = () => {
    return props.submission != null && props.submission.submitted_at != null && props.submission.assignment.settings.resubmit_allowed
  }

  const texts = [
    "Teachers and Students: Access Premium Tools – Upgrade Today!",
    "Don't miss out on our latest features and updates!",
    "Join now and get exclusive access to new tools and resources!"
  ];

  // window.labLayout is bound once, at mount, so the dispatcher would otherwise see the
  // state of the first render; it reads current state through this ref.
  const latestRef = useRef({});
  latestRef.current = { subscription_name, show_ic_creator };

  const instance = (fun, arg = null) => {
    if (fun == "openIcCreator") {
      // CALLED FROM the lab's canvas toolbar (TopMenu.hx)
      if (!latestRef.current.show_ic_creator) return;
      handleOpenIcCreator({ subscription_name: latestRef.current.subscription_name, setIcCreatorOpen });
    }
    // setUseNGSpice true, false
    // setNGSpiceNetList string
    if (fun == "setEditingAssignment") {
      setIsEditingAssignment(arg);
      arg ? $("react-lab-holder").show() : $("react-lab-holder").hide();
      if (arg) setVisible(true);
      setTimeout(() => setLabHeightProperly(), 1);
    }
    if (fun == "clearDescription") {
      setDescription("");
    }
    if (fun == "saveAssignment") {
      // CALLED FROM app/javascript/react/school_admin/src/SubmissionView.js
      save(arg[0], arg[1])
    }

    if (fun == "setUseNGSpice") {

      if (arg && props.is_mobile == false)
        setTimeout(() => {
          setVisible(true);
          setUseNGSpice(arg);
        }, 1000);
    }

    if (fun == "setNGSpiceNetList") {
      // Handle multiple arguments: arg is the netlist, we need to get modelJsons from arguments
      const modelJsons = arguments.length > 2 ? arguments[2] : null;
      setNGSpiceNetList(arg);
      if (modelJsons) {
        setNgSpiceModelJsons(modelJsons);
      }
    }
    if (fun == "setSelectedComponent") {
      setSelectedComponentId(arg);      
    }
    if (fun == "show_lab_alert") {
      const showLabAlert = (text, seconds) => {
        toast.info(text, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: seconds * 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      };
      showLabAlert(arg.text, arg.seconds);
    }


    if (fun == "start_limited") {
      setLimited(true);
      setLimitation(true);
    }

    if (fun == "show_limitation") {
      setLimited(true);
      setLimitation(true);
      setTimeout(() => {
        setLimited(false);
        window.thumbs.setCurrentMouseEvent('up');
      }, 500);
    }
  }

  function now() {
    return (new Date()).getTime()
  }

  function validCircuitName(aTitle) {
    return aTitle && aTitle.length > 3 && aTitle.length < 256
  }

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const wasSaveOpened = usePrevious(isSaveOpened)
  if (wasSaveOpened && !isSaveOpened) {
    window.disable_haxe_events = false;
  }
  if (wasSaveOpened === false && isSaveOpened) {
    window.disable_haxe_events = true;
  }

  // NOTE: This function is NOT used for the main user profile menu in the header.
  // The actual user menu is rendered by Header/components/RightSection/UserMenu.js
  // This function is used for mobile menu and other dropdowns in the lab interface.
  function userMenuChange(e, data) {
    if (!data || !data.value) return;

    if (data.value === "logout") {
      document.getElementById("logout").submit();
    } else if (data.value && data.value.match(/\/users\/\d+\/profile/)) {
      // Open profile links in new tab
      window.open(data.value, '_blank', 'noopener,noreferrer');
    } else {
      window.location = data.value;
    }
  }

  function onZoomMenuChange(e, data) {
    const newZoomValue = Math.max(0.1, Math.min(5, data.value));
    if (window.thumbs && window.thumbs.lab) {
      window.thumbs.lab("newZoom", newZoomValue, [$("#lab-div-id").width(), $("#lab-div-id").height()]);
      setZoomValue(newZoomValue);
    }
  }

  //called when user click Yes on submit answer dialog that appears after auto-save, ANSWER btn ->SAVE btn ->DIALOG->YES btn
  function submitAnswer(aIsRetry = false) {
    if (!user) {
      toast.error(l.please_sign_in_to_submit_answer, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 2000,
        hideProgressBar: true
      });
      return;
    }

    showSubmitedAnswerDialog(false);
    //1. Save the current experiment
    const assignmentId = submission?.assignment?.id || null;
    save(assignmentId, () => {
      // submit the answer now

      setIsAnswering(true);
      setIsSaving(true);
      setSaveOpened(false);
      setRetryOpened(false);
      setCustomError(false);

      // Refresh thumb before submitting
      window.thumbs.refresh_current_thumb();

      let saving_notification_id = toast.loading(l.submiting_answer + '...', { autoClose: false });
      setTimeout(() => setLabHeightProperly(), 1);

      const payload = {
        submission_id: submission.id,
        answer: mark,
        retry: aIsRetry,
        lab_state: window.thumbs.lab_state()  // Add lab state to payload
      };


      axios.post("/api/submit-answer", payload)
        .then(response => {
          //hide saving progress                    
          setIsSaving(false);
          setIsAnswering(false);
          setSubmittedAt(response.data.submitted_at);

          // Show success notification

          toast.update(saving_notification_id, {
            isLoading: false,
            type: "success",
            render: l.your_answer_has_been_saved,
            closeButton: true,
            hideProgressBar: true
          });
          setLabHeightProperly();

          // Handle resubmission case
          if (aIsRetry && response.data.status === 200) {
            setRetryOpened(false);
          }
        })
        .catch(error => {
          toast.update(saving_notification_id, {
            isLoading: false,
            type: "error",
            render: l.error_submitting_answer,
            autoClose: 2000,
            hideProgressBar: true
          });

          setLabHeightProperly();
          if (error.response.data.status == 900) {
            setCustomError(true);
            setIsSaving(false);
            setIsAnswering(false);
          } else {
            setRetryOpened(true);
          }
        });
    });
  }

  function onSave() {
    if (user) {
      const trial_finished = !is_subscription_active && !within_trial_period;
      if (!is_subscription_active) {
        instance("show_limitation");
        return;
      }

      // no need to save for no paid users
      // if (trial_finished || restrict_save ) {
      //     if (restrict_save && !trial_finished){
      //         if (typeof gtag === "function")
      //             gtag('event', 'restrict_save', { 'event_category': 'sales', 'event_label': "Save" });                        
      //     }
      //     instance("show_limitation");
      //     return;
      // }

      setTriedToSave(0)
      window.thumbs.sync();

      com.clientside.main.Html5Bootstrap.jsCall("prepare_thumbs_capture");




      setTimeout(() => {
        window.thumbs.refresh_current_thumb();
      }, 100);


      setSaveSubmited(false);
      setTitleBefore(title);
      setSaveOpened(true);
    } else {
      let offline_circuit = window.thumbs.lab_state()
      localStorage.setItem('offlineCircuit', JSON.stringify(offline_circuit));
      localStorage.setItem('triedToSave', true);
      setTriedToSave(1)
    }
  }


  function saveMark() {
    setMarkCaption("Saving...")
    setIsSaving(true)
    const payload = { submission_id: submission.id, mark }

    axios.post("/api/classroom/store-mark", payload)
      .then(response => {
        //hide saving progress
        setMarkCaption("Saved!")
        setIsSaving(false)
        {
          toast.info(l.mark_saved_successfully, { autoClose: 1000 * 3 })
        }
      }
      )
      .catch(error => {
        setMarkCaption("Retry saving")
        setIsSaving(false)
        toast.warning(l.error_saving_mark, { autoClose: 1000 * 4 })
      })
  }

  function save(assignment_id = null, success_callback = null, aCallSuccessDirectly = false) {
    if (aCallSuccessDirectly) {
      success_callback();
    } else {

      //Is this called to save the assignment ? ex: teacher is saving it.
      const isAssignmentCall = assignment_id != null;

      if (!isAssignmentCall && !submission && !validCircuitName(title)) {
        setSaveSubmited(true)
        return false
      }
      setIsAnswering(false)
      setIsSaving(true)
      setSaveOpened(false)
      setRetryOpened(false)
      let circuits = [...window.thumbs.state.slides]
      let tags = [...window.thumbs.state.tags]
      circuits = circuits.map((slide, index) => ({ annotation_texts: slide.annotation_texts, circuit: slide.data, text: slide.text, component_summary: slide.component_summary }))

      let saving_notification_id = null;
      // if (!isAssignmentCall)
      {
        saving_notification_id = toast.loading(l.saving_circuit + '...', { autoClose: false });
      }

      setTimeout(() => setLabHeightProperly(), 1);

      const thumbnail_base64_data = window.thumbs.state.slides[window.thumbs.state.current_slide].base64_shapshot;

      const payload = {
        tags: tags,
        assignment_id: assignment_id,
        experiment_id,
        temp_experiment_id: !experiment_id ? window.temp_experiment_id : undefined,

        circuits,
        public: is_public,
        title: isAssignmentCall ? "Assignment with id:" + assignment_id.toString() : title,
        thumbnail_base64_data,
        ai_chat_visible: aiVisible
      }

      axios.post("/api/store", payload)
        .then(response => {
          //hide saving progress
          // if (!isAssignmentCall) toast.dismiss();
          setIsSaving(false)
          setIsAnswering(false)
          setExperiment_id(response.data.experiment_id)
          // Rebuild circuit object with fresh share URLs based on the saved experiment
          const freshShareUrl = response.data.share_url;
          const freshTitle = response.data.lab_title;
          const thumbnailUrl = response.data.lab_props?.circuit?.thumbnail_url || circuit.thumbnail_url;

          setCircuit({
            ...response.data.lab_props.circuit,
            lab_title: freshTitle,
            name: freshTitle,
            share_url: freshShareUrl,
            thumbnail_url: thumbnailUrl,
            // Rebuild all derived share URLs with the fresh share_url
            facebook_share: `https://www.facebook.com/sharer.php?u=${freshShareUrl}`,
            twitter_share: `https://www.twitter.com/share?text=${freshShareUrl}`,
            linkedin_share: `http://www.linkedin.com/shareArticle?mini=true&url=${freshShareUrl}`,
            email_share: `mailto:?subject=Welcome to DCACLab - ${freshTitle}&body=View this circuit and simulation on DCACLab: \n${freshShareUrl}`,
            bb_code: `[url=${freshShareUrl}][img]${thumbnailUrl}[/img][/url]`,
            markdown: `[![DCACLab Circuit ](${thumbnailUrl})](${freshShareUrl})`,
            html: `<a href='${freshShareUrl}'><img src='${thumbnailUrl}'/></a>`
          })

          window.lab_props = response.data.lab_props;

          // Update userProjects list so the Load dropdown shows the new experiment
          if (response.data.userProjects) {
            setuserProjects(response.data.userProjects);
          }
          document.title = payload.title;
          com.clientside.main.Html5Bootstrap.jsCall("lab_got_saved");
          if (!isAssignmentCall) firstSession.markSaved();
          // The parameters are the same as pushState.
          if (!isAssignmentCall)
            history.replaceState({ page: 1 }, payload.title, response.data.url);
          // Show success notification
          //if (!isAssignmentCall)                    
          toast.update(saving_notification_id, { isLoading: false, type: "success", render: l.your_circuit_has_been_saved, autoClose: 2000, hideProgressBar: true });
          setLabHeightProperly();
          if (success_callback != null)
            success_callback();

        }
        )
        .catch(error => {
          com.clientside.main.Html5Bootstrap.jsCall("lab_got_saved");
          toast.update(saving_notification_id, { isLoading: false, type: "error", render: l.error_saving_circuit, autoClose: 4000, hideProgressBar: true });
          setLabHeightProperly();
          if (error.response.data.status == 900) {
            setCustomError(true);
            setIsSaving(false);
          } else {
            setRetryOpened(true);
          }
        });
    }
  };

  React.useEffect(() => {
    // Set initial height and adjust when resizing
    const handleResize = () => {
      setLabHeightProperly();
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call initially to set height

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // The board sits right under the top bar: when the bar's height changes (its controls
  // fold or unfold, the lab finishes loading, ...) size the lab again.
  const setLabHeightRef = useRef(null);
  setLabHeightRef.current = setLabHeightProperly;
  React.useEffect(() => {
    const topBar = document.getElementById('top-bar');
    if (!topBar || typeof ResizeObserver === 'undefined') return undefined;
    let lastHeight = topBar.getBoundingClientRect().height;
    const observer = new ResizeObserver(() => {
      const height = topBar.getBoundingClientRect().height;
      if (height === lastHeight) return;
      lastHeight = height;
      if (setLabHeightRef.current) setLabHeightRef.current();
    });
    observer.observe(topBar);
    return () => observer.disconnect();
  }, [isMobile, isEditingAssignment]);

  // Initialize session timeout monitoring
  React.useEffect(() => {
    // Only enable session monitoring for logged-in users
    if (props.session.is_user_signed_in) {
      // Set up warning callback
      sessionManager.onWarning((minutesLeft) => {
        setSessionMinutesLeft(minutesLeft);
        setSessionWarningOpen(true);
        setSessionExpired(false);
      });

      // Set up expiration callback
      sessionManager.onExpired(() => {
        setSessionExpired(true);
        setSessionWarningOpen(true);
      });

      // Set up session restored callback (user signed in on another tab)
      sessionManager.onSessionRestored((data) => {
        // Reset the session expired flag so axios won't block requests
        resetSessionExpiredFlag();
        // Close any session warning modals
        setSessionWarningOpen(false);
        setSessionExpired(false);
        // Show success notification
        import('react-toastify').then(({ toast }) => {
          toast.success('Session restored. You can now save your work.', {
            autoClose: 3000,
            hideProgressBar: true
          });
        });
      });
    }

    // Cleanup on unmount
    return () => {
      if (props.session.is_user_signed_in) {
        sessionManager.stopMonitoring();
      }
    };
  }, [props.session.is_user_signed_in]);

  React.useEffect(() => {
    // Display ASCII art in console
    displayConsoleArt();

    // Initialize here:

    // const isAtMaxWidth = screen.availWidth - window.innerWidth === 0
    // const screenPixelRatio = (window.outerWidth - 8) / window.innerWidth
    // const isAtDefaultZoom = screenPixelRatio > 0.92 && screenPixelRatio <= 1.10
    // const isMaximizedAndDefaultZoom = isAtMaxWidth && isAtDefaultZoom
    // const isFullscreen = window.outerWidth == screen.width && window.outerHeight == screen.height


    if (is_labLoaded && props.session.cta != null) {
      setLimitation(true);
      setCTA(props.session.cta);
      setLimited(true);
    }

    setSubmision(props.submission)
    setIsTeacher(props.isTeacher)
    setCircuit(props.circuit)
    setShowAds(props.show_ads)
    setShowIcCreator(props.show_ic_creator)
    setis_subscription_active(props.session.is_subscription_active)
    setwithin_trial_period(props.session.within_trial_period)
    setRestrict_save(props.session.restrict_save)
    setis_user_signed_in(props.session.is_user_signed_in)
    setSubscription_name(props.session.subscription_name)
    setSlidesCollapsed(false);
    window.subscription_name = props.session.subscription_name;
    window.is_lab_iframe = action_name == 'lab_iframe';

    setMarkCaption(l.set_mark)
    if (props.submission) {
      setMark(props.submission.mark == null ? 0 : props.submission.mark)
      setSubmittedAt(props.submission.submitted_at)
    }
    window.reading_precision = parseInt(props.reading_precision) || 5;
    window.is_mosfet_new = props.is_mosfet_new;
    window.is_ldr_new = props.is_ldr_new;
    window.is_555_new = props.is_555_new;
    window.is_bell_new = props.is_bell_new;
    window.lab_props = props;
    window.view_only_mode = "false";
    if (window.lab_props.submission != null) {
      window.add_new_components_allowed = props.submission.assignment.settings.add_new_components_allowed ? 'true' : 'false'
      window.property_inspector_hidden = props.submission.assignment.settings.property_inspector_hidden ? 'true' : 'false';
      window.current_hidden = props.submission.assignment.settings.current_hidden ? 'true' : 'false';
    }
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);


    window.labLayout = instance;
    $('textarea#description').focus(function () {
      window.disable_haxe_events = true;
    });
    $('textarea#description').on('blur', function (e) {
      window.disable_haxe_events = false;
      window.thumbs.sync();
    });




    window.disable_haxe_events = false;
    setLabLoaded(false)
    setExperiment_id(props.experiment_id)

    setTitle(props.title)
    setuserProjects(props.userProjects)
    setPublic(props.is_public)
    setTriedToSave(0)
    window.embedLab();

    // Set initial AI chat visibility based on both conditions
    if ((props.show_ai_chat && !props.experiment_id) || props.ai_chat_visible === true) {
      setVisible(false);  // Close notes panel if open
      setAiVisible(true); // Show AI panel
    }
  }, []);

  // The useTimeTracker hook now handles logging internally
  // We don't need a separate effect for logging

  // Warn user before leaving/refreshing the page to prevent data loss
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (is_labLoaded) {  // Only warn when lab is loaded and user has potential work
        const message = "Your work might be lost if you haven't saved. Are you sure you want to leave?";
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [is_labLoaded]);

  // Check lab height every 100ms until loaded
  useEffect(() => {
    if (!is_labLoaded) {
      const intervalId = setInterval(() => {
        setLabHeightProperly();
      }, 100);

      // Cleanup interval when component unmounts or when is_labLoaded becomes true
      return () => clearInterval(intervalId);
    }
  }, [is_labLoaded]);

  // Cross-tab session synchronization
  useEffect(() => {
    // Check if SessionSync is available (loaded from application.js)
    if (typeof window.DCACLabSessionSync === 'undefined') {
      console.warn('[Lab] SessionSync not available');
      return;
    }

    // Initialize session sync with current user ID
    const currentUserId = user ? user.id : null;
    window.DCACLabSessionSync.init(currentUserId, {
      enablePolling: true  // Enable polling as fallback
    });

    // Listen for session changes from other tabs/windows
    window.DCACLabSessionSync.onSessionChange((type, userId) => {
      if (type === 'signin' || type === 'signout' || type === 'userswitch') {
        // Fetch fresh user state and update UI dynamically (no page reload)
        axios.post("/api/current-user.json", {})
          .then(response => {
            if (response.data.user && user && response.data.user.id !== user.id) {
              // Different user signed in - redirect to home for safety
              window.location = window.domain_url || '/';
            } else {
              // Same user or sign-in/out - update UI
              user_call_response(response);
            }
          })
          .catch(error => {
            // User signed out or session error - update UI to signed-out state
            setUser(null);
            setis_subscription_active(false);
            setwithin_trial_period(false);
            setis_user_signed_in(false);
            setLabHeightProperly();
            com.clientside.main.Html5Bootstrap.setSubscription(false);
            com.clientside.main.Html5Bootstrap.setSignedIn(false);
            com.clientside.main.Html5Bootstrap.jsCall("within_trial_period", false);
          });
      }
    });

    // Cleanup on unmount
    return () => {
      if (window.DCACLabSessionSync) {
        window.DCACLabSessionSync.destroy();
      }
    };
  }, []); // Run once on mount

  function user_call_response(response) {
    setUser(response.data.user)
    const menuData = response.data.userMenu;
    setUserMenu(Array.isArray(menuData) ? menuData : []);
    setShowIcCreator(response.data['lab_props'].show_ic_creator)
    setis_subscription_active(response.data['session'].is_subscription_active)
    setwithin_trial_period(response.data['session'].within_trial_period)
    setRestrict_save(response.data['session'].restrict_save);
    setis_user_signed_in(response.data['session'].is_user_signed_in);
    setShowAds(response.data['session'].show_ads);
    setSponsorAd((response.data['lab_props'] && response.data['lab_props'].sponsor_ad) || null);
    com.clientside.main.Html5Bootstrap.setSubscription(response.data['session'].is_subscription_active);
    com.clientside.main.Html5Bootstrap.setSignedIn(response.data['session'].is_user_signed_in);
    com.clientside.main.Html5Bootstrap.jsCall("within_trial_period", response.data['session'].within_trial_period);
    setLabHeightProperly();
  }
  // Add this state at the top of your component
  const [lastSimulationErrorTime, setLastSimulationErrorTime] = useState(0);

  // Replace the existing setSimulationError function with this:
  function setSimulationError(error) {
    // Suppress errors that are not valid JSON (e.g., transient 500 error pages during mode switch)
    const errorMessage = typeof error === 'string' ? error : (error && error.message) || "";
    if (errorMessage.includes("SyntaxError") || errorMessage.includes("is not valid JSON") || errorMessage.includes("Unexpected token 'F'")) {
      console.log("[LabLayout] Suppressing transient non-JSON simulation error:", errorMessage);
      return;
    }

    const now = Date.now();
    if (now - lastSimulationErrorTime >= 5000) {
      toast.error(l.error_simulating_circuit, {
        autoClose: 4000, // Toast will close after 5000 milliseconds (5 seconds)
        hideProgressBar: true // Optionally, show the progress bar while the toast is visible
        // Toast library handles error colors automatically
      });
      setLastSimulationErrorTime(now);
    }
  }

  function showToast(options) {
    const { type = 'info', title, description, time = 4000 } = options;

    if (type === 'warning' || type === 'error') {
      toast.error(description || title, {
        autoClose: time,
        hideProgressBar: true
        // Uses var(--destructive) in theme for error styling
      });
    } else if (type === 'success') {
      toast.success(description || title, {
        autoClose: time,
        hideProgressBar: true
        // Uses var(--chart-1) in theme for success styling
      });
    } else if (type === 'lock') {
      toast.warning(description || title, {
        autoClose: time,
        icon: '🔒',
        hideProgressBar: true,
        style: { color: 'var(--secondary-foreground)' }
      });
    } else {
      toast.info(description || title, {
        autoClose: time,
        hideProgressBar: true
      });
    }
  }

  function onLabLoaded() {
    com.clientside.main.Html5Bootstrap.setSubscription(is_subscription_active);
    com.clientside.main.Html5Bootstrap.setSignedIn(is_user_signed_in);
    com.clientside.main.Html5Bootstrap.jsCall("within_trial_period", within_trial_period);
    window.thumbs.lab("setPlotLocation", initialLocation);

    setLabHeightProperly();
    setLabLoaded(true); // Ensure this is set when the lab is ready

    // Install Spark AI bridge — SparkLabAPI (Haxe) sets window.sparkLabAPI
    if (typeof window.SparkLabAPI !== 'undefined' && window.SparkLabAPI.initBridge) {
      window.SparkLabAPI.initBridge();
    }

    // Breadboard supplier lead popup (BREADBOARD_LEAD_POPUP_ENABLED): the prop is true only when the page
    // rendered shared/_breadboard_lead_capture, which defines window.dcacDev.
    if (props.breadboard_lead_popup) {

      if (dcacDev != null && dcacDev.shouldShowStickyFooter()) {
        dcacDev.showStickyFooter();
        setLimited(false);
      }

    }

    // Show segment modal after lab is fully loaded (if the function exists)
    if (typeof window.showSegmentModalWhenReady === 'function') {
      window.showSegmentModalWhenReady();
    }

  }
  function setLabHeightProperly() {


    const div = document.querySelector('#lab-and-description-side');
    if (!div) return; // Guard clause if element doesn't exist

    const rect = div.getBoundingClientRect();
    const topPosition = rect.top;
    const lab_div = document.querySelector('#lab-div-id');
    const firstChild = lab_div?.firstChild;
    if (firstChild) {
      firstChild.style.padding = '0px';
    }
    setLabHeight(`calc(100vh - ${topPosition}px)`);


    if (!is_labLoaded && labLoadingTimerHandler) {
      clearTimeout(labLoadingTimerHandler);
      setLabLoadingTimerHandler(null);
    }
  }
  function handleVisibilityChange(isVisible) {
    if (last_visiblity_change == isVisible) return;
    last_visiblity_change = isVisible;


    if (isVisible) {
      // Check if we should show the subscription modal based on thresholds
      // Only for non-subscribers
      if (!is_subscription_active) {
        // Show modal for secondary threshold if we should
        if (secondaryThresholdReached && shouldShowModalForSecondary) {
          //setShowSubscriptionModal(true);
        }
        // Show modal for primary threshold if we should
        else if (thresholdReached && shouldShowModalForPrimary) {
          //setShowSubscriptionModal(true);
        }
      }

      // Continue with existing user check logic
      if (!user) {
        axios.post("/api/current-user.json", {})
          .then(response => {
            user_call_response(response);
            // enhancement: show save dialog now incase he has tried to save when he was not signed in yet
          }
          )
          .catch(error => {
            setis_subscription_active(false)
            setwithin_trial_period(false)
            setis_user_signed_in(false)
            setTimeout(() => setLabHeightProperly(), 1);
            com.clientside.main.Html5Bootstrap.setSubscription(false);
            com.clientside.main.Html5Bootstrap.setSignedIn(false);
            com.clientside.main.Html5Bootstrap.jsCall("within_trial_period", false);
          })
      }
      if (user) {
        axios.post("/api/current-user.json", {})
          .then(response => {
            if (response.data.user.id != user.id) {
              window.location = domain_url
            } else {
              user_call_response(response);
            }
          }
          )
          .catch(error => {
            if (error.response?.status == 401) {
              setSubmision(null)
              setUser(null)
              setUserMenu(null)

              setis_subscription_active(false)
              setwithin_trial_period(false)
              setis_user_signed_in(false)
              setTimeout(() => setLabHeightProperly(), 1);
              com.clientside.main.Html5Bootstrap.setSubscription(false);
              com.clientside.main.Html5Bootstrap.setSignedIn(false);
              com.clientside.main.Html5Bootstrap.jsCall("within_trial_period", false);
            }
          })
      }
    }
  }

  const contextClass = {
    success: 'toast-success',
    error: 'toast-error',
    info: 'toast-info',
    warning: 'toast-warning',
    default: 'toast-default',
    dark: 'toast-dark',
  };

  // Session warning handlers
  const handleExtendSession = async () => {
    const success = await sessionManager.extendSession();
    if (success) {
      setSessionWarningOpen(false);
      toast.success('Session extended successfully', { autoClose: 2000 });
    }
    return success;
  };

  const handleSessionLogout = () => {
    setSessionWarningOpen(false);
    window.location.href = '/users/sign_in';
  };


  // Add default localization strings
  const l = props.l || window.l || {
    save_circuit: 'Save Circuit',

    please_sign_in_to_submit_answer: 'Please sign in to submit answer',
    submiting_answer: 'Submitting answer...',
    your_answer_has_been_saved: 'Your answer has been saved',
    error_submitting_answer: 'Error submitting answer',
    mark_saved_successfully: 'Mark saved successfully',
    error_saving_mark: 'Error saving mark',
    error_simulating_circuit: 'Error simulating circuit',
    saving_circuit: 'Saving circuit...',
    your_circuit_has_been_saved: 'Your circuit has been saved',
    error_saving_circuit: 'Error saving circuit',
    circuit_invalid_name: 'Invalid circuit name',
    circuit_name: 'Circuit name',
    circuit_name_0: 'Circuit name must be at least 4 characters long',
    circuit_name_1: 'Circuit name must be less than 256 characters long',
    public: 'Public',
    new: 'New',
    save: 'Save',
    save_as: 'Save As',
    cancel: 'Cancel',
    share: 'Share',
    you_are_not_sigend_in: 'You are not signed in',
    create_account_to: 'Create an account to save your circuits',
    sign_in: 'Sign in',
    retry: 'Retry',
    error_submit_answer: 'Error submitting answer',
    error_saving_circuit: 'Error saving circuit',
    set_mark: 'Set mark',
    resubmit_answer: 'Resubmit answer',
    submit_answer: 'Submit answer',
    you_can_not_edit_your_work_after_submission: 'You can not edit your work after submission',
    maximum_circuits_number_reached: 'Maximum circuits number reached!',
    you_can_not_save_more_than_the_number_of_circuits_you_have_saved_so_far_contact_us_to_ask_for_remove_limitation: 'You can not save more than the number of circuits you have saved so far, contact us to ask for remove limitation.',
    or_you_can_remove_some_circuits_that_you_do_not_need_to_keep_and_try_again: 'Or you can remove some circuits that you do not need to keep and try again.',
    ok: 'OK'
  };

  return (
    <ErrorBoundary fallback={LabErrorFallback} name="LabLayout" onError={(error, info) => reportError(error, { section: 'LabLayout' }, info)}>
    <PageVisibility onChange={handleVisibilityChange}>
      <>

        {/* Share Component */}
        <Share
          opened={share_opened}
          contentPageUrl={props.content_page_url}
          l={l}
          circuit={circuit}
          setShareOpen={setShareOpen}
          is_labLoaded={is_labLoaded}
          isSaving={isSaving}
        />

        {nodes.length > 0 &&
          <ErrorBoundary fallback={SectionErrorFallback} name="Chart" onError={(error, info) => reportError(error, { section: 'Chart' }, info)}>
          <Chart currentMouseEvent={currentMouseEvent}
            analysisMode={analysisMode}
            setPickedDcSweepSource={setPickedDCSweepSource}
            pickedDCSweepSource={pickedDCSweepSource}
            DCSweepSources={DCSweepSources}
            setAnalysisMode={setAnalysisMode}
            initialLocation={initialLocation}
            setChartLoading={setChartLoading}
            isChartLoading={isChartLoading}
            initialSelectedLabels={initialChecksSelected}
            setSelectedVoltsValues={setSelectedVoltsValues}
            setSelectedAmperesValues={setSelectedAmperesValues}
            chart_volts_data={chart_volts_data}
            chart_VSweep_values={chart_VSweep_values}
            chart_amperes_data={chart_amperes_data}
            setSelectedNodes={setSelectedNodes}
            nodes={nodes}
            setNodes={setNodes}
            currentable_names={currentable_names}
            metricNodes={metricNodes}
          />
          </ErrorBoundary>
        }

        {!isEditingAssignment && <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />}

        {/* TOP MENU */}
        {!isEditingAssignment && (
          <TopBar
            // Props from parent
            topBarColor={props.topBarColor}
            logo_path={props.logo_path}
            home_path={props.home_path}
            assignment={props.assignment}
            creator={props.creator}
            isTeacher={props.isTeacher}
            propSubmission={props.submission}
            l={l}
            show_ai_chat={props.show_ai_chat}
            action_name={props.action_name}
            has_about={props.has_about}
            is_mobile={isMobile}
            embed_mode={props.embed_mode}
            embed_lab_url={props.embed_lab_url}

            // Mobile slides overlay
            mobileSlidesOpen={mobileSlidesOpen}
            setMobileSlidesOpen={setMobileSlidesOpen}

            // State values
            isSaving={isSaving}
            is_labLoaded={is_labLoaded}
            is_public={is_public}
            setPublic={setPublic}
            setShareOpen={setShareOpen}
            user={user}
            userProjects={userProjects}
            subscription_name={subscription_name}
            // Help menu (links + forum feedback): wherever the old floating button showed
            show_help={!isEditingAssignment && !isMobile && !props.is_breadboard_lab && !props.embed_mode}
            help_l={l.help_menu}
            is_user_signed_in={is_user_signed_in}
            setTriedToSave={setTriedToSave}
            title={title}
            submittedAt={submittedAt}
            submission={submission}
            circuit={circuit}
            mark={mark}
            setMark={setMark}
            markCaption={markCaption}
            setMarkCaption={setMarkCaption}
            zoomValue={zoomValue}
            visible={visible}
            setVisible={setVisible}
            aiVisible={aiVisible}
            setAiVisible={setAiVisible}
            userMenu={userMenu}

            // Functions
            onSave={onSave}
            save={save}
            showSubmitedAnswerDialog={showSubmitedAnswerDialog}
            saveMark={saveMark}
            userMenuChange={userMenuChange}
            onZoomMenuChange={onZoomMenuChange}

            // Computed values
            show_save_buttons={show_save_buttons}
          />
        )}
        {/* SAMPLE CIRCUITS */}
        {!isEditingAssignment && <>
          {/* Phones: the thumbnails row (#internal-ad-off) is hidden, so the
              sponsor banner gets a full-width row of its own above the lab. */}
          {sponsorSlot && isMobile && sponsorAd &&
            <div id="sponsor-row-mobile" className="sponsor-row sponsor-row--mobile">
              <SponsorBanner sponsor_ad={sponsorAd} l={l} onLayoutChange={setLabHeightProperly} />
            </div>
          }
          {/* paddingTop 14 only cancels the navbar grid's -14px bottom margin; the rows carry
              no padding of their own, so the thumbnails / sponsor row sits flush against the toolbar. */}
          <Grid style={{ margin: 0, padding: 0, paddingTop: isMobile ? 0 : 14, marginTop: isMobile ? -1 : 0 }} id="internal-ad-off">
            {action_name === 'lab' && (user == false || user == null) && !isMobile &&
              (props.featured_experiments.length > 0 || show_ads || sponsorAd) &&
              <Grid.Row style={{ padding: 0 }}>
                <Grid.Column width={16}>
                  <ShowOff
                    shining_ad={props.shining_ad}
                    show_ads={show_ads}
                    is_mobile={isMobile}
                    featured_experiments={props.featured_experiments}
                    sponsor_ad={sponsorAd}
                    l={l}
                    onSponsorLayoutChange={setLabHeightProperly}
                  />
                </Grid.Column>
              </Grid.Row>
            }

            {/* A circuit's page: the sponsor banner alone (no sample circuits or AdSense), centred. */}
            {action_name === 'show' && sponsorSlot && !isMobile && sponsorAd &&
              <Grid.Row style={{ padding: 0 }}>
                <Grid.Column width={16}>
                  <div id="sponsor-row-experiment" className="sponsor-row sponsor-row--standalone">
                    <SponsorBanner sponsor_ad={sponsorAd} l={l} onLayoutChange={setLabHeightProperly} />
                  </div>
                </Grid.Column>
              </Grid.Row>
            }

            {/* The signed-in row only carries ads now (AdSense or the sponsor banner); no ad, no row. */}
            {action_name === 'lab' && user && !isMobile && (show_ads || sponsorAd) &&
              <Grid.Row style={{ padding: 0 }}>
                <Grid.Column width={16}>
                  <SignedInAd
                    show_ads={show_ads}
                    sponsor_ad={sponsorAd}
                    l={l}
                    onSponsorLayoutChange={setLabHeightProperly}
                  />
                </Grid.Column>
              </Grid.Row>
            }
          </Grid>
        </>}

        {/* Lab And Description Side Grid */}
        <Grid id='lab-and-description-side' style={{
          padding: 0,
          position: 'relative',
          margin: 0,
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: isMobile ? 'var(--card)' : background,
          marginLeft: 0
        }}>
          {/* THUMBS COLUMN */}
          {(user || force_show_thumbs) && (
            <div id='thumbs-div-id' style={{
              width: (!isMobile && !slidesHidden) ? '140px' : '0px',
              position: 'absolute',
              left: 0,
              top: 6,
              bottom: 6,
              backgroundColor: isMobile ? 'var(--card)' : background,
              zIndex: 2,
              overflowY: 'auto',
              overflowX: 'hidden',
              visibility: (!isMobile && !slidesHidden) ? 'visible' : 'hidden',
              borderRight: !isMobile ? '1px solid var(--sidebar-border)' : 'none',
              marginTop: 0,
              paddingLeft: '3px',
              paddingRight: '3px'
            }}>
              <div style={{
                width: slidesHidden ? 0 : '100%',
                paddingTop: 0,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div style={{ flex: '1 1 auto' }}>
                  <ErrorBoundary fallback={SectionErrorFallback} name="Circuit Slides" onError={(error, info) => reportError(error, { section: 'LeftPanel' }, info)}>
                  <LeftPanel
                    current_locale={current_locale}
                    set_currentable_names={set_currentable_names}
                    subscription_name={subscription_name}
                    setPickedDCSweepSource={setPickedDCSweepSource}
                    setMetricNodes={setMetricNodes}
                    setSimulationError={setSimulationError}
                    showToast={showToast}
                    setZoomValue={setZoomValue}
                    setDCSweepSources={setDCSweepSources}
                    setAnalysisMode={setAnalysisMode}
                    setCurrentMouseEvent={setCurrentMouseEvent}
                    setInitialLocation={setInitialLocation}
                    setChartLoading={setChartLoading}
                    setInitialChecksSelected={setInitialChecksSelected}
                    setCurrentSlide={setCurrentSlide}
                    forecolor={forecolor}
                    submission={submission}
                    is_mobile={props.is_mobile}
                    user={user}
                    user_ics={user_ics}
                    official_ics={official_ics}
                    experiment_owner_ics={experiment_owner_ics}
                    setNodes={setNodes}
                    setVSweepValues={setVSweepValues}
                    setSelectedVoltsValues={setSelectedVoltsValues}
                    setSelectedAmperesValues={setSelectedAmperesValues}
                    setVisible={setVisible}
                    getDescription={() => { return description; }}
                    setDescription={setDescription}
                    getQuestion={() => { return question }}
                    setQuestion={setQuestion}
                    setLabLoaded={setLabLoaded}
                    onLabLoaded={onLabLoaded}
                    is_labLoaded={is_labLoaded}
                    formattedTime={formattedTime}
                    thresholdReached={thresholdReached}
                  />
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          )}

          {/* MAIN CONTENT GRID */}
          <Grid.Column
            width={16}
            style={{
              padding: 0,
              margin: 0,
              height: labHeight,
              position: 'relative',
              // Use width calc instead of marginLeft to prevent overflow
              width: (!isMobile && !slidesHidden) ? 'calc(100% - 140px)' : '100%',
              marginLeft: (!isMobile && !slidesHidden) ? '140px' : 0,
              maxWidth: '100vw',
              // A flex item defaults to min-width:auto, so the canvas' px width kept this column from
              // shrinking with the window: it stayed capped at 100vw while offset 140px, pushing the
              // lab's right edge (scrollbar, rightmost components) off-screen until a reload.
              minWidth: 0,
              boxSizing: 'border-box',
              marginTop: 0
            }}
          >
            {/* Rest of the lab content */}
            <Grid style={{
              margin: 0,
              height: '100%',
              position: 'relative'
            }}>
              {/* LAB COLUMN */}
              <Grid.Column
                width={notesOpen || aiVisible ? 12 : (show_ads ? 14 : 16)}
                style={{
                  height: '100%',
                  padding: 0,
                  paddingTop: 0,
                  margin: 0,
                  position: 'relative',
                  minWidth: 0 // same as the column above: let the canvas shrink with the window
                }}
              >
                <div id="lab-div-id" style={{
                  backgroundColor: 'var(--card)',
                  height: '100%',
                  marginRight: notesOpen || aiVisible ? 0 : (show_ads ? 15 : 0),
                  width: '100%',
                  position: 'relative'
                }}>
                  {!is_labLoaded && (
                    <>
                      <style>
                        {`@keyframes loadingDot {
                                                    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                                                    40% { opacity: 1; transform: scale(1); }
                                                }`}
                      </style>
                      <div style={{
                        position: 'absolute',
                        top: 'calc(50% + 80px)',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px',
                        pointerEvents: 'none',
                        zIndex: 9999
                      }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--muted-foreground)', animation: 'loadingDot 1.4s ease-in-out infinite', animationDelay: '0s' }}></span>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--muted-foreground)', animation: 'loadingDot 1.4s ease-in-out infinite', animationDelay: '0.2s' }}></span>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--muted-foreground)', animation: 'loadingDot 1.4s ease-in-out infinite', animationDelay: '0.4s' }}></span>
                        </div>
                        <span style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>{l.loading_please_wait}</span>
                      </div>
                    </>
                  )}
                  {firstSessionLayerShown && firstSessionPlacement && (
                    <div
                      id="first-session-layer"
                      dir={firstSessionRTL ? 'rtl' : 'ltr'}
                      style={{
                        position: 'absolute',
                        // One column at the inline-end edge, under the parts palette, clear of the canvas
                        // centre and stopping above the guest CTA whenever they would meet (useLayerPlacement)
                        top: firstSessionPlacement.top,
                        [firstSessionRTL ? 'left' : 'right']: 12,
                        width: firstSessionPlacement.width,
                        maxHeight: firstSessionPlacement.maxHeight,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 8,
                        zIndex: 900, // under ActionMessage (1000) and modals
                        pointerEvents: 'none' // only the cards take clicks, never the gaps
                      }}
                    >
                      {firstSession.showChecklist && props.first_session_checklist === true && (
                        <FirstSessionChecklist
                          stepIds={firstSession.stepIds}
                          steps={firstSession.steps}
                          current_locale={current_locale}
                          isMobile={isMobile}
                          compact={firstSessionPlacement.compact}
                          onDismiss={firstSession.dismissChecklist}
                        />
                      )}
                      {showStarterPanel && (
                        <StarterPanel
                          starters={starterCircuits}
                          current_locale={current_locale}
                          compact={firstSessionPlacement.compact}
                          onDismiss={handleDismissStarterPanel}
                        />
                      )}
                    </div>
                  )}
                </div>


                {

                  !props.embed_mode &&
                  (action_name !== "iframe" &&
                    ((cta != null) || (!is_subscription_active && limitation)) || (!is_subscription_active)) &&
                  <>
                    {is_labLoaded &&
                      <ErrorBoundary fallback={SectionErrorFallback} name="Action Message" onError={(error, info) => reportError(error, { section: 'ActionMessage' }, info)}>
                      <ActionMessage 
                        cta={cta}
                        user={user}
                        segment={user?.segment}
                        onOpenInviteTeacherModal={() => setInviteTeacherModalOpen(true)}
                        cheapest_plan={props.cheapest_plan}
                        min_plan_price={props.min_plan_price}
                        current_locale={current_locale}
                        setLimited={val => setLimited(val)}
                        setLimitation={val => setLimitation(val)} current_slide={currentSlide} l={l} limited={(is_labLoaded && limited) || cta != null} looking_at_others_circuit={props.looking_at_others_circuit} />
                      </ErrorBoundary>
                    }
                  </>

                }


              </Grid.Column>

              {/* NOTES PANEL */}
              {notesOpen && !aiVisible && (
                <Grid.Column width={4} style={{
                  backgroundColor: 'var(--card)',
                  color: 'var(--card-foreground)',
                  height: labHeight,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: 'var(--shadow-md)',
                  padding: '1em',
                  transition: 'all 0.3s ease'
                }}>
                  {(false && action_name != 'assignment' && !isEditingAssignment) && (
                    <Button
                      icon="close"
                      size="mini"
                      style={{
                        position: 'absolute',
                        right: '0.5em',
                        top: '0.5em',
                        zIndex: 1
                      }}
                      onClick={() => setVisible(false)}
                    />
                  )}

                  <ErrorBoundary fallback={SectionErrorFallback} name="Notes" onError={(error, info) => reportError(error, { section: 'SideTabs' }, info)}>
                  <SideTabs
                    setVisible={setVisible}
                    show_save_buttons={show_save_buttons}
                    question_description={question}
                    description={description}
                    labPageNotSigned={!is_user_signed_in && action_name == 'lab'}
                    isCreator={action_name == 'lab' || props.creator && user && props.creator.id == user.id}
                    isStudentAnswering={(action_name == 'assignment' && user != null && !props.isTeacher && (submittedAt === null || props.submission.assignment.settings.resubmit_allowed) && user && submission && submission.user_id == user.id)}
                    setDescription={(c) => { setDescription(c); }}
                    onSave={onSave}
                    NGSpiceNetList={NGSpiceNetList}
                    ngSpiceModelJsons={ngSpiceModelJsons}
                    selectedComponentId={selectedComponentId}                    
                    isEditingAssignment={isEditingAssignment}
                    ngSpiceSelected={useNGSpice && (description == null || (description != null && description.length < 10)) && submission == null}
                    useNGSpice={useNGSpice && (submission == null || isTeacher)}
                  />
                  </ErrorBoundary>
                </Grid.Column>
              )}

              {/* AI PANEL */}
              {(props.show_ai_chat || props.ai_chat_visible) && aiVisible && (
                <Grid.Column width={4} style={{
                  backgroundColor: 'var(--card)',
                  color: 'var(--card-foreground)',
                  overflowY: 'auto',
                  boxShadow: 'var(--shadow-md)',
                  padding: 0,
                  transition: 'all 0.3s ease'
                }}>
                  {/* <Button
                                        icon="close"
                                        size="mini"
                                        style={{
                                            position: 'absolute',
                                            right: '0.5em',
                                            top: '0.5em',
                                            zIndex: 1
                                        }}
                                        onClick={() => setAiVisible(false)}
                                    /> */}
                  {props.show_ai_chat && is_labLoaded  && <div id="spark-chat" data-server-url={sparkServerUrl} data-provider="glm" style={{ height: '100%', width: '100%' }}></div>}
                </Grid.Column>
              )}
            </Grid>
          </Grid.Column>

          {/* MOBILE SLIDES OVERLAY */}
          {isMobile && (
            <MobileSlidesOverlay
              open={mobileSlidesOpen}
              onClose={() => setMobileSlidesOpen(false)}
              l={l}
            />
          )}
        </Grid>

        {/* SUBMIT ANSWER DIALOG */}
        <Modal dimmer="inverted" size='small' open={submitAnswerDialogShown}>

          <Modal.Header>{isResubmit() ? 'Resubmit' : 'Submit'} Your Assignment Now?</Modal.Header>

          <Modal.Content>
            <p>Are you sure you want to {isResubmit() ? 'resubmit' : 'submit'} your assignment?</p>
            {props.submission != null && !props.submission.assignment.settings.resubmit_allowed &&
              <p>You can not edit your work after submission.</p>
            }
          </Modal.Content>
          <Modal.Actions>
            <Button style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }} onClick={() => showSubmitedAnswerDialog(false)}>
              No
            </Button>
            <Button style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} onClick={() => {
              window.thumbs.refresh_current_thumb();
              setTimeout(() => submitAnswer(), 1);
            }}>
              Yes
            </Button>
          </Modal.Actions>
        </Modal>

        {/* SAVE DIALOG */}
        <Modal dimmer="inverted" size='small' open={isSaveOpened} style={{ borderRadius: '10px', overflow: 'hidden' }}>
          <Header
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              padding: '10px 20px',
              margin: 0,
              fontSize: '1.2em'
            }}
            icon={<Icon name='save' style={{ color: 'var(--primary-foreground)' }} />}
            content={l.save_circuit}
          />
          <Form warning={isSaveSubmited && !validCircuitName(title)} id="save_form" error style={{ padding: 20 }} >
            <Form.Field>
              <label>{l.circuit_name}</label>
              <Form.Input padding={20} autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    save()
                  }
                }}
                error={validCircuitName(title) ? false : isSaveSubmited ? true : false}
                onChange={(e, data) => {
                  setTitle(data.value);
                }}
                className="lab-input"
                style={{ width: '100%', height: 32 }}
                placeholder={l.circuit_name + '...'} value={title} />
            </Form.Field>

            <Message
              warning
              header={l.circuit_invalid_name}
              list={[
                l.circuit_name_0,
                l.circuit_name_1
              ]}
            />


            <Modal.Actions style={{ textAlign: 'right', paddingTop: 10 }}>

              <Button style={{ visibility: 'hidden' }} />
              <Button active={false} style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }} onClick={(e) => { setSaveOpened(false); setTitle(titleBefore); }}>
                {l.cancel}
              </Button>
              <Button active style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} onClick={() => save()}>
                {l.save}
              </Button>
            </Modal.Actions>

          </Form>
        </Modal>





        {/* TRIED SAVING WHILE NOT SIGNED IT */}
        <Modal
          basic
          onClose={() => { setTriedToSave(2) }}
          open={triedToSave == 1}
          style={{ backgroundColor: '#2d2723', border: '1px solid #5c524a', borderRadius: '8px' }}
          size='mini'
        >
          <Header icon style={{ color: '#E8E0D8' }}>
            <Icon name='sign-in' color="brown" />
            {l.you_are_not_sigend_in}
          </Header>
          <Modal.Content style={{ color: '#E8E0D8' }}>
            <p>{l.create_account_to}</p>
          </Modal.Content>
          <Modal.Actions id="need_to_save_actions" style={{ backgroundColor: 'transparent' }}>
            <Button style={{ background: '#5c524a', color: '#E8E0D8', border: '1px solid #E8E0D8' }} onClick={() => { setTriedToSave(2); window.open("users/sign_in", "_blank"); }}>
              {l.sign_in}
            </Button>
          </Modal.Actions>
        </Modal>

        {/* CUSTOM ERROR */}
        <Modal
          centered
          dimmer="inverted"
          open={customErrorOpened}
        >
          <Modal.Header>Maximum circuits number reached!</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <p>You can't save more than the number of circuits you have saved so far, contact us to ask for remove limitation.</p>
              <p>Or you can remove some circuits that you don't need to keep and try again.</p>

            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} onClick={() => setCustomError(false)}>OK</Button>
          </Modal.Actions>
        </Modal>




        {/* RE-TRY SAVING ERROR */}
        <Confirm
          open={isRetryOpened}
          confirmButton={l.retry}
          onCancel={() => { setIsSaving(false), setRetryOpened(false) }}
          content={isAnswering ? l.error_submit_answer : l.error_saving_circuit}
          onConfirm={() => {
            //retry answer? or retry save?
            if (isAnswering)
              submitAnswer(true)
            else
              save()
          }}
          size='small'
        />



        {/* Subscription Modal 
                
                I removed it as I got worried according to:
                https://www.perplexity.ai/search/what-are-best-practices-for-de-ojepC_PWTfea_Zv2kg2qcg
                
                
                */

        }
        {action_name !== 'assignment' && action_name !== 'index' && (
          <SubscriptionModal
            open={showSubscriptionModal}
            cheapest_plan={props.cheapest_plan}
            onClose={() => setShowSubscriptionModal(false)}
            isSecondaryThreshold={secondaryThresholdReached}
            isTertiaryThreshold={false}
            current_locale={current_locale}
            onContinue={() => {
              // Disable showing the modal for primary threshold
              setShouldShowModalForPrimary(false);

              // If we're already past the secondary threshold, disable that too
              if (elapsedTime >= SECONDARY_THRESHOLD) {
                setShouldShowModalForSecondary(false);
              }

              // Reset the thresholds in the hook
              resetPrimaryThreshold();
              if (elapsedTime >= SECONDARY_THRESHOLD) {
                resetSecondaryThreshold();
              }
            }}
          />
        )}

        {/* Session Timeout Warning Modal */}
        <SessionWarningModal
          open={sessionWarningOpen}
          minutesLeft={sessionMinutesLeft}
          onExtend={handleExtendSession}
          onLogout={handleSessionLogout}
          isExpired={sessionExpired}
          current_locale={current_locale}
        />

        {/* Invite your teacher (student CTA in ActionMessage) */}
        <InviteTeacherModal
          open={inviteTeacherModalOpen}
          onClose={() => setInviteTeacherModalOpen(false)}
          current_locale={current_locale}
          user={user}
        />

        {/* IC Creator Modal */}
        {show_ic_creator && <Modal
          open={isIcCreatorOpen}
          onClose={() => {
            // Close the modal
            setIcCreatorOpen(false);

            // Fetch the ICs from the backend
            axios.get('/api/ics/fetch_ics')
              .then(response => {
                const { official_ics, user_ics } = response.data;

                // Inject the ICs into the lab
                if (window.com && window.com.clientside && window.com.clientside.main && window.com.clientside.main.Html5Bootstrap) {
                  window.com.clientside.main.Html5Bootstrap.jsCall("set_official_ics", official_ics);
                  window.com.clientside.main.Html5Bootstrap.jsCall("set_user_ics", user_ics);
                  com.clientside.main.Html5Bootstrap.jsCall("clear_ics");
                }
              })
              .catch(error => {
                console.error('Error fetching ICs:', error);
              });
          }}
          size="large"
          dimmer="inverted"
          style={{
            height: '95vh',
            width: '90%',
            margin: 'auto',
            position: 'absolute',
            top: '2.5%',
            left: '5%',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-xl)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 30001
          }}
          closeIcon={{ name: 'close', color: 'grey', style: { top: '10px', right: '10px', fontSize: '20px', cursor: 'pointer' } }}
        >
          <Modal.Header style={{
            borderBottom: '1px solid var(--border)',
            padding: '10px 20px',
            backgroundColor: 'var(--muted)',
            borderTopLeftRadius: 'var(--radius)',
            borderTopRightRadius: 'var(--radius)',
            flex: '0 0 auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name="microchip" size="large" style={{ marginRight: '10px', color: 'var(--primary)' }} />
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--foreground)' }}>{l.integrated_circuit_builder}</span>
            </div>
          </Modal.Header>
          <Modal.Content style={{
            padding: 0,
            flex: '1 1 auto',
            height: 'calc(95vh - 70px)',
            overflow: 'hidden',
            backgroundColor: 'var(--card)'
          }}>
            <ErrorBoundary fallback={SectionErrorFallback} name="IC Creator" onError={(error, info) => reportError(error, { section: 'IcCreator' }, info)}>
            <IcCreatorWrapper user={user} setTriedToSave={setTriedToSave} current_locale={current_locale} />
            </ErrorBoundary>
          </Modal.Content>
        </Modal>
        }

        {/* JSON Forms Editor Overlay - Non-invasive API for parent components to trigger schema-based editing */}
        {editJson && (
          <JsonFormsEditor
            open={!!editJson}
            schema={editJson.schema}
            uiSchema={editJson.uiSchema}
            data={editJson.data}
            title={editJson.title || 'Edit Properties'}
            locale={current_locale}
            onSave={(updatedJson) => {
              if (editJson.onSave) {
                editJson.onSave(updatedJson);
              }
            }}
            onClose={() => setEditJson(null)}
          />
        )}

      </>
    </PageVisibility>
    </ErrorBoundary>
  )

}

if (typeof window !== 'undefined') {
  window.LabLayout = LabLayout;
}

export default LabLayout;
